
import React, { useState, useEffect } from 'react';
import { Compass, Sparkles, MapPin, Calendar, Clock, Loader2, ArrowRight, Info, Search, Check, AlertCircle, ExternalLink, ShieldCheck, Globe } from 'lucide-react';
import { calculateBirthChart, suggestLocations } from '../services/geminiService';
import { BirthData, ChartResult, PlanetaryPlacement, LocationSuggestion } from '../types';
import StarField from './StarField';

const ZODIAC_ORDER = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const ZODIAC_SYMBOLS: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓'
};

const PLANET_SYMBOLS: Record<string, string> = {
  Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂', 
  Jupiter: '♃', Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇'
};

const ChartCalculator: React.FC = () => {
  const [data, setData] = useState<BirthData>({ name: '', date: '', time: '', location: '' });
  const [result, setResult] = useState<ChartResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'planets' | 'houses'>('planets');
  
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (data.location.length > 3 && !isSearchingLocation && !result) {
        setIsSearchingLocation(true);
        const results = await suggestLocations(data.location);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setIsSearchingLocation(false);
      } else if (data.location.length <= 3) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [data.location, result]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const chart = await calculateBirthChart(data);
      setResult(chart);
    } catch (error) {
      alert("The stars are clouded. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const selectSuggestion = (s: LocationSuggestion) => {
    setData({ ...data, location: s.name, lat: s.lat, lng: s.lng });
    setShowSuggestions(false);
  };

  const getAngleForCusp = (sign: string, degree: number) => {
    const signIndex = ZODIAC_ORDER.indexOf(sign);
    if (signIndex === -1) return 0;
    return (signIndex * 30) + degree;
  };

  const getPlanetAngle = (placement: PlanetaryPlacement) => {
    const signIndex = ZODIAC_ORDER.indexOf(placement.sign);
    if (signIndex === -1) return 0;
    return (signIndex * 30) + placement.degree + (placement.minutes / 60) + (placement.seconds / 3600);
  };

  return (
    <section id="calculator" className="py-24 bg-slate-950 relative overflow-hidden">
      <StarField count={40} />
      
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-amber-400 text-sm tracking-[0.2em] uppercase mb-2">Cosmic Identity</h2>
          <h3 id="calculator-title" className="text-4xl md:text-5xl font-serif text-slate-50">Natal Chart Interpretation</h3>
          <div className="w-24 h-1 bg-amber-500 mx-auto mt-6" />
          <p className="mt-4 text-indigo-400/60 text-xs max-w-lg mx-auto italic flex items-center justify-center gap-2">
            <ShieldCheck size={14} className="text-amber-500/50" />
            High-precision astronomical engine grounded in verified ephemeris data.
          </p>
        </div>

        {!result ? (
          <div className="max-w-2xl mx-auto bg-slate-900 border border-indigo-900/50 p-8 md:p-12 shadow-2xl rounded-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-indigo-400 mb-2 font-semibold">Soul's Name</label>
                  <input
                    type="text" required
                    className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 focus:outline-none focus:border-amber-500 rounded-sm transition-all"
                    placeholder="Enter your name"
                    value={data.name}
                    onChange={e => setData({...data, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-indigo-400 mb-2 flex items-center gap-2 font-semibold">
                    <Calendar size={12}/> Birth Date
                  </label>
                  <input
                    type="text" required placeholder="YYYY-MM-DD"
                    className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 focus:outline-none focus:border-amber-500 rounded-sm"
                    value={data.date}
                    onChange={e => setData({...data, date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-indigo-400 mb-2 flex items-center gap-2 font-semibold">
                    <Clock size={12}/> Birth Time
                  </label>
                  <input
                    type="text" required placeholder="HH:MM (24h)"
                    className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 focus:outline-none focus:border-amber-500 rounded-sm"
                    value={data.time}
                    onChange={e => setData({...data, time: e.target.value})}
                  />
                </div>
                <div className="col-span-2 relative">
                  <label className="block text-xs uppercase tracking-widest text-indigo-500 font-bold mb-2 flex items-center gap-2 font-semibold">
                    <MapPin size={12}/> Birth Location
                  </label>
                  <div className="relative">
                    <input
                      type="text" required
                      className={`w-full bg-slate-950 border ${showSuggestions ? 'border-amber-500' : 'border-indigo-900'} p-4 text-indigo-100 focus:outline-none focus:border-amber-500 rounded-sm transition-all pr-12`}
                      placeholder="City, Country"
                      value={data.location}
                      autoComplete="off"
                      onChange={e => setData({...data, location: e.target.value})}
                      onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-500">
                      {isSearchingLocation ? <Loader2 size={18} className="animate-spin text-amber-500" /> : <Search size={18} />}
                    </div>
                  </div>

                  {showSuggestions && (
                    <div className="absolute z-50 w-full mt-2 bg-slate-950 border border-amber-500/50 shadow-2xl rounded-sm overflow-hidden animate-fade-in">
                      <div className="p-2 border-b border-indigo-900/30 bg-slate-900 flex justify-between items-center">
                        <span className="text-[10px] uppercase tracking-widest text-amber-500 font-bold">Precise Origin Found</span>
                        <button type="button" onClick={() => setShowSuggestions(false)} className="text-indigo-500 hover:text-white text-xs">Close</button>
                      </div>
                      <ul className="max-h-48 overflow-y-auto">
                        {suggestions.map((s, idx) => (
                          <li key={idx}>
                            <button
                              type="button"
                              onClick={() => selectSuggestion(s)}
                              className="w-full text-left px-4 py-3 text-sm text-indigo-100 hover:bg-amber-500 hover:text-slate-950 transition-colors flex items-center justify-between group"
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{s.name}</span>
                                <span className="text-[10px] uppercase tracking-tighter opacity-50">Lat: {s.lat.toFixed(4)}, Lng: {s.lng.toFixed(4)}</span>
                              </div>
                              <Check size={14} className="opacity-0 group-hover:opacity-100" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-widest transition-all rounded-sm flex items-center justify-center gap-3 group shadow-[0_0_20px_rgba(245,158,11,0.2)]"
              >
                {loading ? <Loader2 className="animate-spin" /> : <Sparkles size={20} className="group-hover:rotate-12 transition-transform" />}
                {loading ? 'Performing Celestial Calculation...' : 'Map My Soul'}
              </button>
            </form>
          </div>
        ) : (
          <div className="animate-fade-in space-y-12">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              
              {/* Left Column: The Graph / Wheel */}
              <div className="lg:col-span-5 flex flex-col items-center">
                <div className="relative w-full max-w-[500px] aspect-square">
                  <svg viewBox="0 0 100 100" className="w-full h-full chart-wheel drop-shadow-[0_0_30px_rgba(30,58,138,0.3)]">
                    <circle cx="50" cy="50" r="48" fill="#020617" stroke="#1e1b4b" strokeWidth="0.5" />
                    
                    {ZODIAC_ORDER.map((sign, i) => {
                      const angle = i * 30;
                      return (
                        <g key={sign}>
                          <path 
                            d={`M 50 50 L ${50 + 48 * Math.cos((angle - 90) * Math.PI / 180)} ${50 + 48 * Math.sin((angle - 90) * Math.PI / 180)} A 48 48 0 0 1 ${50 + 48 * Math.cos((angle + 30 - 90) * Math.PI / 180)} ${50 + 48 * Math.sin((angle + 30 - 90) * Math.PI / 180)} Z`}
                            fill={i % 2 === 0 ? "rgba(30,58,138,0.1)" : "transparent"}
                          />
                          <text 
                            x="50" y="8" 
                            fontSize="3.5" 
                            textAnchor="middle" 
                            fill="#fbbf24" 
                            className="font-serif opacity-60"
                            transform={`rotate(${angle + 15}, 50, 50)`}
                          >
                            {ZODIAC_SYMBOLS[sign]}
                          </text>
                        </g>
                      )
                    })}

                    <circle cx="50" cy="50" r="42" fill="none" stroke="#312e81" strokeWidth="0.5" />

                    {result.houses.map((house, i) => {
                      const angle = getAngleForCusp(house.sign, house.degree);
                      return (
                        <g key={i}>
                          <line 
                            x1="50" y1="50" 
                            x2="50" y2="8" 
                            stroke={i === 0 ? "#fbbf24" : "#1e40af"} 
                            strokeWidth={i === 0 ? "0.4" : "0.2"} 
                            transform={`rotate(${angle}, 50, 50)`} 
                          />
                          <text 
                             x="50" y="44" 
                             fontSize="2.5" 
                             textAnchor="middle" 
                             fill="#6366f1"
                             transform={`rotate(${angle + 15}, 50, 50)`}
                          >
                            {house.houseNumber}
                          </text>
                        </g>
                      );
                    })}

                    {result.placements.map((p, idx) => {
                      const angle = getPlanetAngle(p);
                      return (
                        <g key={idx} transform={`rotate(${angle}, 50, 50)`}>
                          <circle cx="50" cy="22" r="1.2" fill="#fbbf24" className="animate-pulse" />
                          <text 
                            x="50" y="19" 
                            fontSize="4.5" 
                            textAnchor="middle" 
                            fill="#fbbf24" 
                            className="font-bold drop-shadow-sm"
                            transform={`rotate(${-angle}, 50, 19)`}
                          >
                            {PLANET_SYMBOLS[p.planet] || p.planet[0]}
                          </text>
                        </g>
                      );
                    })}

                    <circle cx="50" cy="50" r="8" fill="#020617" stroke="#fbbf24" strokeWidth="0.5" />
                    <text x="50" y="51.5" fontSize="4" textAnchor="middle" fill="#fbbf24" className="font-serif font-bold">EARTH</text>
                  </svg>
                  
                  <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex gap-6 text-[10px] uppercase tracking-widest text-indigo-400/80 bg-slate-900/50 px-4 py-2 rounded-full border border-indigo-900/30">
                    <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500"/> Planets</span>
                    <span className="flex items-center gap-1.5"><div className="w-4 h-0.5 bg-indigo-600"/> Houses</span>
                    <span className="flex items-center gap-1.5"><div className="w-4 h-0.5 bg-amber-500"/> Ascendant</span>
                  </div>
                </div>

                <div className="mt-20 w-full space-y-4">
                  <div className="p-6 bg-slate-900/80 border border-indigo-900/50 rounded-sm shadow-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-amber-500/10 rounded-full">
                        <ShieldCheck className="text-amber-500" size={18}/>
                      </div>
                      <h4 className="text-amber-400 text-xs uppercase tracking-widest font-bold">Verified Alignment</h4>
                    </div>
                    <div className="grid grid-cols-3 gap-4 border-t border-indigo-900/30 pt-4">
                      <div>
                        <p className="text-[10px] uppercase text-indigo-500 mb-1">Sun</p>
                        <p className="font-serif text-lg text-white leading-tight">{result.sunSign}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-indigo-500 mb-1">Moon</p>
                        <p className="font-serif text-lg text-white leading-tight">{result.moonSign}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-indigo-500 mb-1">Rising</p>
                        <p className="font-serif text-lg text-white leading-tight">{result.risingSign}</p>
                      </div>
                    </div>
                  </div>

                  {result.sources && (
                    <div className="p-4 bg-indigo-950/30 border border-indigo-900/50 rounded-sm">
                      <h5 className="text-[10px] uppercase tracking-widest text-indigo-400 mb-3 flex items-center gap-2">
                        <ExternalLink size={12}/> Calculation Sources
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {result.sources.map((src, i) => (
                          <a 
                            key={i} 
                            href={src.uri} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[9px] bg-slate-950 border border-indigo-800 hover:border-amber-500 px-2 py-1 rounded-sm text-indigo-300 transition-colors"
                          >
                            {src.title}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-sm flex gap-3 items-start">
                    <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] text-amber-200/60 leading-relaxed italic">
                      Disclaimer: This chart is generated using advanced AI astronomical logic and verified web data. For a certified deep-dive reading, we recommend booking a session with Allie.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column: Detailed Lists */}
              <div className="lg:col-span-7">
                <div className="mb-8">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h4 className="text-2xl md:text-3xl font-serif text-amber-50 mb-1">{data.name}'s Cosmic Blueprint</h4>
                      <p className="text-xs uppercase tracking-[0.2em] text-indigo-400 flex items-center gap-2">
                        Calculated by Allie's Cosmic Engine
                      </p>
                    </div>
                    <div className="text-right flex flex-col items-end">
                      <div className="flex items-center gap-2 text-indigo-400 text-[10px] uppercase tracking-widest mb-1">
                        <MapPin size={10}/> Precise Origin
                      </div>
                      <p className="text-sm text-white font-medium bg-indigo-950/40 px-3 py-1 border border-indigo-900/50 rounded-sm mb-1">{data.location}</p>
                      <div className="flex items-center gap-2 text-amber-500/80 font-mono text-[10px]">
                        <Globe size={10}/> 
                        {result.coordinates.lat.toFixed(4)}°N, {result.coordinates.lng.toFixed(4)}°E
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 to-transparent opacity-50" />
                    <p className="text-indigo-100/90 leading-relaxed font-light text-lg italic pl-4">
                      "{result.summary}"
                    </p>
                  </div>
                </div>

                <div className="flex border-b border-indigo-900/30 mb-8 overflow-x-auto">
                  <button 
                    onClick={() => setActiveTab('planets')}
                    className={`px-6 py-4 text-[11px] uppercase tracking-[0.2em] transition-all whitespace-nowrap relative ${activeTab === 'planets' ? 'text-amber-400 font-bold' : 'text-indigo-400 hover:text-indigo-200'}`}
                  >
                    Precision Placements
                    {activeTab === 'planets' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400" />}
                  </button>
                  <button 
                    onClick={() => setActiveTab('houses')}
                    className={`px-6 py-4 text-[11px] uppercase tracking-[0.2em] transition-all whitespace-nowrap relative ${activeTab === 'houses' ? 'text-amber-400 font-bold' : 'text-indigo-400 hover:text-indigo-200'}`}
                  >
                    House Custodians
                    {activeTab === 'houses' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400" />}
                  </button>
                </div>

                {activeTab === 'planets' ? (
                  <div className="grid gap-6 animate-fade-in pb-12">
                    {result.placements.map((p, idx) => (
                      <div key={idx} className="bg-slate-900/50 border border-indigo-900/30 p-5 hover:border-amber-500/30 transition-all group rounded-sm shadow-md">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-5">
                            <span className="text-4xl text-amber-400 group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(245,158,11,0.3)]">
                              {PLANET_SYMBOLS[p.planet] || '✨'}
                            </span>
                            <div>
                              <h5 className="text-[10px] uppercase tracking-widest text-indigo-400 font-bold mb-1">{p.planet}</h5>
                              <p className="font-serif text-xl text-white">
                                {p.degree}° {p.minutes}' {p.seconds}" <span className="text-amber-400/80">{p.sign}</span>
                              </p>
                            </div>
                          </div>
                          <div className="px-3 py-1 bg-indigo-950/60 text-[10px] uppercase text-amber-400 tracking-widest border border-indigo-800/50 rounded-full">
                            House {p.house}
                          </div>
                        </div>
                        <p className="text-sm text-indigo-100/70 leading-relaxed border-t border-indigo-900/20 pt-4 italic font-light">
                          {p.description}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-6 animate-fade-in pb-12">
                    {result.houses.map((h, idx) => (
                      <div key={idx} className="bg-slate-900/50 border border-indigo-900/30 p-5 hover:border-amber-500/30 transition-all flex gap-6 rounded-sm shadow-md">
                        <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-indigo-950/80 border border-indigo-800 text-amber-400 font-serif text-2xl rounded-sm">
                          {h.houseNumber}
                        </div>
                        <div>
                          <div className="flex items-baseline gap-4 mb-2">
                            <h5 className="font-serif text-2xl text-white">{h.sign}</h5>
                            <span className="text-xs text-indigo-400 font-mono tracking-tighter">{h.degree}° {h.minutes}' {h.seconds}" Cusp</span>
                          </div>
                          <p className="text-sm text-indigo-100/70 leading-relaxed italic font-light">
                            {h.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pt-8 border-t border-indigo-900/30 flex flex-col sm:flex-row gap-4 sticky bottom-0 bg-slate-950/80 backdrop-blur-sm py-4">
                  <button 
                    onClick={() => setResult(null)}
                    className="px-6 py-4 border border-indigo-800 text-indigo-300 hover:text-white transition-colors text-xs uppercase tracking-widest bg-slate-950 rounded-sm"
                  >
                    Calculate New Chart
                  </button>
                  <a 
                    href="#booking"
                    className="flex-1 py-4 bg-amber-500 text-slate-950 font-bold text-xs uppercase tracking-widest hover:bg-amber-400 transition-all flex items-center gap-3 justify-center rounded-sm shadow-lg"
                  >
                    Deepen Your Insights with Allie <ArrowRight size={16}/>
                  </a>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ChartCalculator;
